<!--
  AI:
  Status: "confirmed"
  Type: Component
  SubType: CrudIntegration
  Reason: Top-down creation of one cp.ProvisionContract plus 1..N cp.ProvisionContractAgent rows.
          Both mvs-crud-object forms run in CAPTURE ONLY mode (hideDefaultActions) - the component
          owns the save gate and the write sequence itself. This file contains NO hand-built form
          controls: every entity field is rendered by the metadata form.
-->
<p-dialog
        [header]="hasExistingContract() ? 'Provisionssatz anlegen' : 'Provisionszeile anlegen'"
        [visible]="visible"
        (visibleChange)="handleVisibleChange($event)"
        (onShow)="handleShow()"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [closable]="!state.saveBusy"
        [style]="{width: '62rem'}"
        [breakpoints]="{'960px': '92vw'}"
        styleClass="cp-provision-contract-create-wizard">

    @if (!initialized) {

        <p-skeleton height="18rem"></p-skeleton>

    } @else if (completed) {

        <!-- ══════════════════════ Ergebnis ══════════════════════ -->
        <div class="flex flex-column gap-4">

            <div class="flex align-items-center gap-3 p-3 surface-ground border-round-lg">
                <i class="fa-solid fa-circle-check text-2xl text-green-500"></i>
                <div class="flex flex-column">
                    <span class="font-semibold">
                        {{ hasExistingContract() ? 'Provisionssätze angelegt' : 'Provisionszeile angelegt' }}
                    </span>
                    <span class="text-color-secondary text-sm">
                        @if (hasExistingContract()) {
                            {{ state.agentDrafts.length }} Provisionssatz/Provisionssätze wurden zur
                            bestehenden Provisionszeile angelegt. Die Zeile selbst wurde nicht verändert.
                        } @else {
                            {{ state.agentDrafts.length }} Provisionssatz/Provisionssätze wurden dazu angelegt.
                        }
                        Alle Sätze sind manuell und werden von künftigen Läufen weder überschrieben
                        noch gelöscht.
                    </span>
                </div>
            </div>

            <ng-container [ngTemplateOutlet]="deferredPanel"></ng-container>
        </div>

    } @else {

        <div class="cp-wizard-steps">

            <!-- ══════════════════════ Kontext ══════════════════════ -->
            @if (provisionAgentName) {
                <div class="flex flex-wrap align-items-center gap-2">
                    <span class="text-color-secondary text-sm">Abrechnungskonto der Liste</span>
                    <span class="font-medium">{{ provisionAgentName }}</span>
                </div>
            }

            <!-- Optimistic by default; this banner appears only after the SERVER refused with 403 -->
            @if (!canAgentChange) {
                <p-message severity="warn" styleClass="w-full">
                    <span>
                        Der Server hat den Vorgang verweigert: Ihnen fehlt das Recht
                        <strong>cp_agentChange</strong>.
                    </span>
                </p-message>
            }

            <!-- ══════════════════════ Schritt 1: Provisionszeile ══════════════════════ -->
            @if (isStepDecided('contract')) {

                <div class="cp-wizard-step">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('contract') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionszeile</h3>
                            <span class="cp-step-subtitle">Vertrag, Provisionsart und Ereignis</span>
                        </div>

                        <div class="cp-step-status cp-step-completed">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>{{ hasExistingContract() ? 'Vorgegeben' : 'Erfasst' }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content">
                        <div class="cp-step-result">
                            <div class="cp-result-icon">
                                <i class="fa-regular fa-file-contract"></i>
                            </div>

                            <div class="cp-result-details">
                                <span class="cp-result-label">
                                    {{ hasExistingContract() ? 'Bestehende Provisionszeile' : 'Provisionszeile' }}
                                </span>
                                <span class="cp-result-value">{{ getContractSummary() }}</span>
                            </div>

                            @if (canChangeStep('contract')) {
                                <p-button
                                        label="Ändern"
                                        icon="fa-regular fa-pen"
                                        severity="secondary"
                                        [text]="true"
                                        (onClick)="handleChangeStep('contract')">
                                </p-button>
                            }
                        </div>
                    </div>
                </div>
            }

            @if (isStepActive('contract')) {

                <div class="cp-wizard-step cp-wizard-step-active">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('contract') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionszeile</h3>
                            <span class="cp-step-subtitle">
                                @if (hasExistingContract()) {
                                    Die bestehende Zeile, zu der die Sätze angelegt werden
                                } @else {
                                    Vertrag, Provisionsart und Ereignisdatum der neuen Zeitscheibe
                                }
                            </span>
                        </div>

                        <div class="cp-step-status"
                             [class.cp-step-completed]="hasExistingContract()"
                             [class.cp-step-in-progress]="!hasExistingContract()">
                            @if (hasExistingContract()) {
                                <i class="fa-solid fa-lock"></i>
                                <span>Vorgegeben</span>
                            } @else {
                                <span>Schritt {{ getStepNumber('contract') }} von {{ state.steps.length }}</span>
                            }
                        </div>
                    </div>

                    <div class="cp-step-content flex flex-column gap-3">

                        @if (hasExistingContract()) {

                            <!--
                              READ ONLY context, never a form. The row already exists; this step only
                              shows WHAT the new Provisionssätze will hang on. Editing it is the job
                              of the adjust wizard, and creating a second time slice would be a
                              different act that the uniqueness rule of the parent would refuse.
                            -->
                            @if (contractLoadBusy) {
                                <p-skeleton height="5rem" borderRadius="0.5rem"></p-skeleton>
                            } @else if (contractLoadError) {
                                <div class="flex align-items-center gap-2 p-3 surface-ground border-round-lg">
                                    <i class="fa-solid fa-triangle-exclamation text-red-600"></i>
                                    <span class="text-red-600 flex-1">{{ contractLoadError }}</span>
                                    <p-button
                                            label="Erneut versuchen"
                                            icon="fa-regular fa-rotate-right"
                                            severity="secondary"
                                            [text]="true"
                                            (onClick)="handleRetryContractLoad()">
                                    </p-button>
                                </div>
                            } @else {
                                <p-message severity="info" styleClass="w-full">
                                    <span>
                                        Die Provisionszeile besteht bereits und wird nicht verändert.
                                        Vertrag, Provisionsart und Ereignis der neuen Sätze werden aus
                                        ihr übernommen.
                                    </span>
                                </p-message>

                                <div class="cp-step-result">
                                    <div class="cp-result-icon">
                                        <i class="fa-regular fa-file-contract"></i>
                                    </div>

                                    <div class="cp-result-details">
                                        <span class="cp-result-label">Bestehende Provisionszeile</span>
                                        <span class="cp-result-value">{{ getContractSummary() }}</span>
                                    </div>

                                    <p-tag severity="secondary" value="Unverändert"></p-tag>
                                </div>
                            }

                        } @else {

                            <p-message severity="info" styleClass="w-full">
                                <span>
                                    Es wird noch nichts gespeichert. Provisionszeile und Provisionssätze
                                    werden erst im letzten Schritt gemeinsam angelegt.
                                </span>
                            </p-message>

                            @if (isOutsideRunSelection()) {
                                <p-message severity="warn" styleClass="w-full">
                                    <span>{{ getSelectionWarning() }}</span>
                                </p-message>
                            }

                            <!-- AI: CRUD Object Component for cp.ProvisionContract, CAPTURE ONLY -->
                            <mvs-crud-object
                                    [objectType]="'cp.ProvisionContract'"
                                    [defaultCreateDto]="contractDefaultDto"
                                    [formControlOverwrite]="contractFormControlOverwrite"
                                    [importObjectContextDto]="formImportObjectContext"
                                    [hideDefaultActions]="true"
                                    (onFormChange)="handleContractFormChange($event)"
                                    (onFormSubmitStateChange)="handleContractSubmitStateChange($event)"
                                    >
                            </mvs-crud-object>
                        }
                    </div>
                </div>
            }

            <!-- ══════════════════════ Schritt 2: Provisionssätze ══════════════════════ -->
            @if (isStepDecided('agents')) {

                <div class="cp-wizard-step">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('agents') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionssätze</h3>
                            <span class="cp-step-subtitle">Berater und Provisionsart je Satz</span>
                        </div>

                        <div class="cp-step-status cp-step-completed">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>{{ state.agentDrafts.length }} erfasst</span>
                        </div>
                    </div>

                    <div class="cp-step-content">
                        <div class="cp-step-result">
                            <div class="cp-result-icon">
                                <i class="fa-regular fa-users"></i>
                            </div>

                            <div class="cp-result-details">
                                <span class="cp-result-label">Provisionssätze</span>
                                <span class="cp-result-value">
                                    {{ state.agentDrafts.length }} Satz/Sätze erfasst
                                </span>
                            </div>

                            @if (canChangeStep('agents')) {
                                <p-button
                                        label="Ändern"
                                        icon="fa-regular fa-pen"
                                        severity="secondary"
                                        [text]="true"
                                        (onClick)="handleChangeStep('agents')">
                                </p-button>
                            }
                        </div>
                    </div>
                </div>
            }

            @if (isStepActive('agents')) {

                <div class="cp-wizard-step cp-wizard-step-active">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('agents') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionssätze</h3>
                            <span class="cp-step-subtitle">
                                Mindestens ein Satz je Berater und Provisionsart
                            </span>
                        </div>

                        <div class="cp-step-status cp-step-in-progress">
                            <span>Schritt {{ getStepNumber('agents') }} von {{ state.steps.length }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content flex flex-column gap-3">

                        <p-message severity="info" styleClass="w-full">
                            <span>
                                Vertrag, Provisionsart und Ereignis werden aus der Provisionszeile
                                übernommen und sind deshalb nicht erfassbar. Jeder Satz wird als
                                <strong>manuell</strong> angelegt und von künftigen Berechnungsläufen
                                weder überschrieben noch gelöscht.
                            </span>
                        </p-message>

                        <!-- Bereits erfasste Sätze -->
                        @if (state.agentDrafts.length) {
                            <div class="flex flex-column gap-2">
                                <h4>Erfasste Provisionssätze</h4>

                                @for (draft of state.agentDrafts; track draft.key) {
                                    <div class="cp-draft-row"
                                         [class.cp-draft-row-failed]="state.failedDraftKey === draft.key">

                                        <div class="cp-result-icon">
                                            @if (draft.created) {
                                                <i class="fa-solid fa-circle-check text-green-500"></i>
                                            } @else {
                                                <i class="fa-regular fa-user-tie"></i>
                                            }
                                        </div>

                                        <div class="cp-result-details">
                                            <span class="cp-result-label">Provisionssatz</span>
                                            <span class="cp-result-value">
                                                {{ getAgentDraftSummary(draft) }}
                                            </span>
                                        </div>

                                        @if (draft.created) {
                                            <p-tag severity="success" value="Angelegt"></p-tag>
                                        } @else {
                                            <p-button
                                                    label="Entfernen"
                                                    icon="fa-regular fa-trash"
                                                    severity="danger"
                                                    [text]="true"
                                                    [disabled]="state.saveBusy"
                                                    (onClick)="handleRemoveAgentDraft(draft.key)">
                                            </p-button>
                                        }
                                    </div>
                                }
                            </div>
                        } @else {
                            <div class="flex flex-column align-items-center gap-2 p-4 surface-ground border-round-lg">
                                <i class="fa-regular fa-user-plus text-2xl text-color-secondary"></i>
                                <span class="font-semibold">Noch kein Provisionssatz erfasst</span>
                                <span class="text-color-secondary text-sm">
                                    Erfassen Sie unten den ersten Satz und übernehmen Sie ihn in die Liste.
                                </span>
                            </div>
                        }

                        <!-- Erfassung des nächsten Satzes -->
                        <h4>Neuen Provisionssatz erfassen</h4>

                        <!-- AI: CRUD Object Component for cp.ProvisionContractAgent, CAPTURE ONLY -->
                        <mvs-crud-object
                                [objectType]="'cp.ProvisionContractAgent'"
                                [defaultCreateDto]="agentDefaultDto"
                                [formControlOverwrite]="agentFormControlOverwrite"
                                [importObjectContextDto]="formImportObjectContext"
                                [hideDefaultActions]="true"
                                (onFormChange)="handleAgentFormChange($event)"
                                (onFormSubmitStateChange)="handleAgentSubmitStateChange($event)"
                                >
                        </mvs-crud-object>

                        <div class="flex justify-content-end">
                            <p-button
                                    label="Satz übernehmen"
                                    icon="fa-regular fa-plus"
                                    severity="secondary"
                                    [outlined]="true"
                                    [disabled]="!canAddAgentDraft()"
                                    (onClick)="handleAddAgentDraft()">
                            </p-button>
                        </div>
                    </div>
                </div>
            }

            <!-- ══════════════════════ Schritt 3: Prüfen und anlegen ══════════════════════ -->
            @if (isStepActive('summary')) {

                <div class="cp-wizard-step cp-wizard-step-active">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('summary') }}</div>

                        <div class="cp-step-title">
                            <h3>Prüfen und anlegen</h3>
                            <span class="cp-step-subtitle">
                                Was jetzt geschrieben wird und was der nächste Lauf ergänzt
                            </span>
                        </div>

                        <div class="cp-step-status cp-step-in-progress">
                            <span>Schritt {{ getStepNumber('summary') }} von {{ state.steps.length }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content flex flex-column gap-3">

                        <div class="cp-step-result">
                            <div class="cp-result-icon">
                                <i class="fa-regular fa-file-contract"></i>
                            </div>

                            <div class="cp-result-details">
                                <span class="cp-result-label">
                                    {{ hasExistingContract() ? 'Bestehende Provisionszeile' : 'Provisionszeile' }}
                                </span>
                                <span class="cp-result-value">{{ getContractSummary() }}</span>
                            </div>

                            @if (hasExistingContract()) {
                                <p-tag severity="secondary" value="Unverändert"></p-tag>
                            }
                        </div>

                        @for (draft of state.agentDrafts; track draft.key) {
                            <div class="cp-draft-row">
                                <div class="cp-result-icon">
                                    <i class="fa-regular fa-user-tie"></i>
                                </div>

                                <div class="cp-result-details">
                                    <span class="cp-result-label">Provisionssatz</span>
                                    <span class="cp-result-value">{{ getAgentDraftSummary(draft) }}</span>
                                </div>
                            </div>
                        }

                        <ng-container [ngTemplateOutlet]="deferredPanel"></ng-container>
                    </div>
                </div>
            }
        </div>

        <!-- Save failure: everything already written is kept, the retry resumes there -->
        @if (state.saveError) {
            <div class="flex flex-column gap-2 p-3 surface-ground border-round-lg mt-3">
                <p-message severity="error" [text]="state.saveError" styleClass="w-full"></p-message>

                @if (state.saveHint) {
                    <span class="text-sm text-color-secondary">{{ state.saveHint }}</span>
                }

                @if (state.hasPartialResult()) {
                    <span class="text-sm text-color-secondary">
                        Bereits angelegte Daten bleiben erhalten. „Erneut versuchen“ setzt genau an der
                        fehlgeschlagenen Stelle fort und legt nichts doppelt an.
                    </span>

                    <!--
                      The consequence of the OTHER choice, stated before it is made: the row is not
                      removed on cancel, and it occupies its time slice from then on.
                    -->
                    <span class="text-sm text-color-secondary">
                        Bei „Abbrechen“ bleibt die bereits angelegte Provisionszeile bestehen und belegt
                        ihren Zeitraum — ein neuer Anlauf mit denselben Eckdaten wird dann abgewiesen.
                    </span>
                }
            </div>
        }
    }

    <!-- ══════════════════════ Abschluss: die beiden Feldgruppen ══════════════════════ -->
    <!--
      The honesty duty, rendered as data and not as a banner - and split into its two halves,
      because they are NOT the same promise:

        DEFERRED - four nullable fields of the Provisionszeile that are never asked. The pending
                   sentence stands, because the next run really does compute them.
        CAPTURED  - eligibility plus the five liability fields. Their columns are NOT NULL without a
                   default, so the wizard asks for them; the value the user gave is shown and the
                   pending sentence must NOT appear over it, which would call their own input
                   provisional.

      Neither group ever renders a blank cell - that is the failure mode this panel exists to
      prevent, because an empty "Haftungsbeginn" reads as a defect.
    -->
    <ng-template #deferredPanel>
        <div class="flex flex-column gap-3">

            <!-- ── Was der nächste Lauf ergänzt ── -->
            <div class="cp-deferred-panel flex flex-column gap-3">

                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-calculator text-color-secondary"></i>
                    <span class="font-semibold">Was der nächste Berechnungslauf ergänzt</span>
                </div>

                <!--
                  BT-12: the EXPLANATION lives here, once. Per field only the factual statement
                  ("nicht berechnet") - see cp-provision-pending-text.ts for why a per-field promise
                  would be untrue in two of the three states an empty deferred field can be in.
                -->
                <span class="text-sm text-color-secondary">
                    {{ deferredExplanation }}
                    @if (hasExistingContract()) {
                        Was auf der bestehenden Zeile bereits gefüllt ist, steht mit seinem Wert.
                    } @else {
                        Beim manuellen Anlegen wird nichts gerechnet.
                    }
                </span>

                @if (isOutsideRunSelection()) {
                    <p-message severity="warn" styleClass="w-full">
                        <span>{{ getSelectionWarning() }}</span>
                    </p-message>
                }

                <div class="flex flex-column gap-2">
                    <h4>{{ hasExistingContract() ? 'Provisionszeile (bestehend)' : 'Provisionszeile' }}</h4>

                    @for (field of getDeferredContractFields(); track field.key) {
                        <div class="cp-deferred-row">
                            <span class="cp-deferred-label">{{ field.label }}</span>

                            @if (field.captured) {
                                <span class="cp-deferred-value">{{ field.value }}</span>
                            } @else {
                                <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                            }

                            <p-tag severity="info" [value]="field.hint"></p-tag>
                        </div>
                    }
                </div>

                <!--
                  The SATZ level. Without this block the panel would name pending fields on the
                  Provisionszeile only, and a manually created Provisionssatz would make no pending
                  statement at all - while the obligation is to make it on BOTH levels.
                -->
                @for (draft of state.agentDrafts; track draft.key) {
                    <div class="flex flex-column gap-2">
                        <h4>Provisionssatz — {{ getAgentDraftSummary(draft) }}</h4>

                        @for (field of getDeferredAgentFields(draft); track field.key) {
                            <div class="cp-deferred-row">
                                <span class="cp-deferred-label">{{ field.label }}</span>

                                @if (field.captured) {
                                    <span class="cp-deferred-value">{{ field.value }}</span>
                                } @else {
                                    <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                                }

                                <p-tag severity="info" [value]="field.hint"></p-tag>
                            </div>
                        }
                    </div>
                }
            </div>

            <!-- ── Ihre Eingaben ── -->
            <div class="cp-captured-panel flex flex-column gap-3">

                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-keyboard text-color-secondary"></i>
                    <span class="font-semibold">Ihre Angaben</span>
                </div>

                <span class="text-sm text-color-secondary">
                    @if (hasExistingContract()) {
                        Pflichtangaben, die nicht gerechnet werden. Die der Sätze setzen Sie in diesem
                        Vorgang; die der Zeile stehen bereits und bleiben unverändert.
                    } @else {
                        Diese Felder sind Pflichtangaben der Zeile beziehungsweise des Satzes und werden
                        nicht gerechnet, sondern von Ihnen gesetzt.
                    }
                </span>

                <div class="flex flex-column gap-2">
                    <h4>{{ hasExistingContract() ? 'Provisionszeile (bestehend)' : 'Provisionszeile' }}</h4>

                    @for (field of getCapturedContractFields(); track field.key) {
                        <div class="cp-deferred-row">
                            <span class="cp-deferred-label">{{ field.label }}</span>

                            @if (field.captured) {
                                <span class="cp-deferred-value">{{ field.value }}</span>
                            } @else {
                                <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                            }

                            <p-tag
                                    [severity]="hasExistingContract() ? 'secondary' : 'success'"
                                    [value]="field.hint">
                            </p-tag>
                        </div>
                    }
                </div>

                @for (draft of state.agentDrafts; track draft.key) {
                    <div class="flex flex-column gap-2">
                        <h4>Provisionssatz — {{ getAgentDraftSummary(draft) }}</h4>

                        @for (field of getCapturedAgentFields(draft); track field.key) {
                            <div class="cp-deferred-row">
                                <span class="cp-deferred-label">{{ field.label }}</span>

                                @if (field.captured) {
                                    <span class="cp-deferred-value">{{ field.value }}</span>
                                } @else {
                                    <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                                }

                                <p-tag severity="success" [value]="field.hint"></p-tag>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    </ng-template>

    <ng-template pTemplate="footer">

        @if (completed) {

            <p-button
                    label="Schließen"
                    icon="fa-regular fa-xmark"
                    severity="primary"
                    (onClick)="handleClose()">
            </p-button>

        } @else {

            <p-button
                    label="Abbrechen"
                    icon="fa-regular fa-xmark"
                    severity="secondary"
                    [text]="true"
                    [disabled]="state.saveBusy"
                    (onClick)="handleCancel()">
            </p-button>

            @if (canGoBack()) {
                <p-button
                        label="Zurück"
                        icon="fa-regular fa-arrow-left"
                        severity="secondary"
                        [outlined]="true"
                        (onClick)="handleBack()">
                </p-button>
            }

            @if (!state.isLastStep()) {
                <p-button
                        label="Weiter"
                        icon="fa-regular fa-arrow-right"
                        iconPos="right"
                        severity="primary"
                        [disabled]="!canGoForward()"
                        (onClick)="handleForward()">
                </p-button>
            } @else if (state.saveError) {
                <p-button
                        label="Erneut versuchen"
                        icon="fa-regular fa-rotate-right"
                        severity="primary"
                        [loading]="state.saveBusy"
                        [disabled]="state.saveBusy"
                        (onClick)="handleRetrySave()">
                </p-button>
            } @else {
                <p-button
                        label="Anlegen"
                        icon="fa-regular fa-check"
                        severity="primary"
                        [loading]="state.saveBusy"
                        [disabled]="!canSave()"
                        [pTooltip]="getSubmitTooltip()"
                        tooltipPosition="top"
                        (onClick)="handleSave()">
                </p-button>
            }
        }
    </ng-template>

</p-dialog>
